import { useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";

export interface NavigationSource {
  source: string | null;
  sourceType: string | null;
}

const PARAM = "from";

/**
 * The read happens in a lazy initializer (render phase) and the strip in an
 * effect (commit phase), so that StrictMode's second render pass, and any
 * discarded concurrent render, sees the same unstripped URL as the first and
 * commits the same value. A `useRef` guard cannot do this: StrictMode hands
 * the second pass a fresh `{current: null}`, so the guard never survives long
 * enough to matter.
 *
 * Every lazy initializer in a commit runs before any effect in that commit,
 * so callers mounted together on the same page still agree with each other.
 * A caller that mounts later, once an earlier effect has already stripped the
 * marker, still reads nulls. This narrows that failure mode. It does not
 * remove it.
 */
export function useNavigationSource(): NavigationSource {
  const [value] = useState<NavigationSource>(() => {
    const raw = new URLSearchParams(window.location.search).get(PARAM);
    if (raw == null || raw === "") return { source: null, sourceType: null };

    const [source, sourceType = null] = raw.split(":");
    return { source: source ?? null, sourceType };
  });

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!searchParams.has(PARAM)) return;

    // Stripped through react-router's own setter, not a raw
    // history.replaceState, so its cached location loses the marker too
    // instead of only the address bar.
    const next = new URLSearchParams(searchParams);
    next.delete(PARAM);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return value;
}

/**
 * Writer counterpart to `useNavigationSource`. Kept beside the reader so the
 * `from` param's name and its `source:sourceType` format are owned in one
 * place rather than re-encoded at each call site.
 */
export function withNavigationSource(
  route: string,
  source: string,
  sourceType: string
): string {
  const separator = route.includes("?") ? "&" : "?";
  return `${route}${separator}${PARAM}=${source}:${encodeURIComponent(sourceType)}`;
}
